(function () {
  "use strict";

  if (window.yocSchoolContext) {
    return;
  }

  const CONTEXT_VERSION = 1;
  const SESSION_COOKIE_KEY = "yoc_browser_session";
  const ACTIVE_SCHOOL_KEY = "yoc_active_school_context";
  const CONTEXT_EVENT_KEY = "yoc_school_context_event";
  const PENDING_TARGET_KEY = "yoc_pending_school_target";
  const CHANGE_MODE_KEY = "yoc_school_change_mode";
  const NAVIGATION_ERROR_KEY = "yoc_school_navigation_error";
  const CHANNEL_NAME = "yoc-school-context";
  const LEGACY_SCHOOL_KEYS = [
    "selectedSchool",
    "yoc_school_name",
    "yoc_school_location",
    "yoc_school_code",
    "yoc_school_country",
    "yoc_name_list_enabled",
  ];

  const listeners = new Set();
  const receivedEventIds = new Set();
  const runtimeId = createId();
  let channel = null;

  function createId() {
    if (
      window.crypto &&
      typeof window.crypto.randomUUID === "function"
    ) {
      return window.crypto.randomUUID();
    }

    return [
      Date.now().toString(36),
      Math.random().toString(36).slice(2),
      Math.random().toString(36).slice(2),
    ].join("-");
  }

  function readCookie(name) {
    const prefix = `${encodeURIComponent(name)}=`;
    const cookies = String(document.cookie || "").split(";");

    for (let index = 0; index < cookies.length; index += 1) {
      const cookie = cookies[index].trim();
      if (cookie.indexOf(prefix) === 0) {
        try {
          return decodeURIComponent(cookie.slice(prefix.length));
        } catch (error) {
          return "";
        }
      }
    }

    return "";
  }

  function writeSessionCookie(name, value) {
    const secureAttribute =
      window.location && window.location.protocol === "https:"
        ? "; Secure"
        : "";

    document.cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}; Path=/; SameSite=Lax${secureAttribute}`;
  }

  function deleteSessionCookie(name) {
    const secureAttribute =
      window.location && window.location.protocol === "https:"
        ? "; Secure"
        : "";

    document.cookie = `${encodeURIComponent(name)}=; Max-Age=0; Path=/; SameSite=Lax${secureAttribute}`;
  }

  function readStorage(storage, key) {
    try {
      return storage.getItem(key);
    } catch (error) {
      return null;
    }
  }

  function writeStorage(storage, key, value) {
    try {
      storage.setItem(key, value);
      return true;
    } catch (error) {
      return false;
    }
  }

  function removeStorage(storage, key) {
    try {
      storage.removeItem(key);
      return true;
    } catch (error) {
      return false;
    }
  }

  function parseJson(value) {
    if (!value) {
      return null;
    }

    try {
      return JSON.parse(value);
    } catch (error) {
      return null;
    }
  }

  function normalizeSchool(school) {
    if (!school || typeof school !== "object") {
      return null;
    }

    const handle = String(school.handle || "").trim();
    if (!handle || handle.toLowerCase() === "all") {
      return null;
    }

    const rawNameListEnabled =
      school.nameListEnabled != null
        ? school.nameListEnabled
        : school.name_list_enabled;
    let nameListEnabled = null;

    if (rawNameListEnabled === true || rawNameListEnabled === false) {
      nameListEnabled = rawNameListEnabled;
    } else if (rawNameListEnabled != null && rawNameListEnabled !== "") {
      const normalizedValue = String(rawNameListEnabled).trim().toLowerCase();
      if (["true", "1", "yes", "on"].indexOf(normalizedValue) !== -1) {
        nameListEnabled = true;
      } else if (
        ["false", "0", "no", "off"].indexOf(normalizedValue) !== -1
      ) {
        nameListEnabled = false;
      }
    }

    return {
      handle,
      name: String(school.name || "").trim(),
      location: String(school.location || "").trim(),
      code: String(school.code || "").trim(),
      country:
        String(school.country || "US").trim().toUpperCase() === "CA"
          ? "CA"
          : "US",
      nameListEnabled,
    };
  }

  function getSessionId() {
    return readCookie(SESSION_COOKIE_KEY);
  }

  function hasSession() {
    return getSessionId() !== "";
  }

  function ensureSession() {
    const existingSessionId = getSessionId();
    if (existingSessionId) {
      return existingSessionId;
    }

    const sessionId = createId();
    writeSessionCookie(SESSION_COOKIE_KEY, sessionId);
    return sessionId;
  }

  function getStoredContext() {
    return parseJson(readStorage(window.localStorage, ACTIVE_SCHOOL_KEY));
  }

  function getActiveSchool() {
    const sessionId = getSessionId();
    if (!sessionId) {
      return null;
    }

    const context = getStoredContext();
    if (
      !context ||
      context.version !== CONTEXT_VERSION ||
      context.sessionId !== sessionId
    ) {
      return null;
    }

    return normalizeSchool(context.school);
  }

  function schoolsMatch(firstSchool, secondSchool) {
    const first = normalizeSchool(firstSchool);
    const second = normalizeSchool(secondSchool);

    if (!first || !second) {
      return false;
    }

    if (first.handle === second.handle) {
      return true;
    }

    return Boolean(first.code && second.code && first.code === second.code);
  }

  function classifySchoolRequest(activeSchool, requestedSchool) {
    const active = normalizeSchool(activeSchool);
    const requested = normalizeSchool(requestedSchool);

    if (!active && !requested) {
      return "NO_SCHOOL";
    }
    if (!active) {
      return "NEW_SCHOOL";
    }
    if (!requested) {
      return "ACTIVE_SCHOOL";
    }
    if (schoolsMatch(active, requested)) {
      return "SAME_SCHOOL";
    }

    return "SCHOOL_CONFLICT";
  }

  function normalizeIdentityValue(value) {
    return String(value || "").trim().toLowerCase();
  }

  function normalizeCodeIdentity(value) {
    return normalizeIdentityValue(value).replace(/[^a-z0-9]/g, "");
  }

  function isServiceFeeItem(item) {
    const title = normalizeIdentityValue(
      item && (item.product_title || item.title),
    );
    const properties = (item && item.properties) || {};
    const productType = normalizeIdentityValue(properties._yoc_product_type);
    return title.indexOf("service fee") === 0 || productType === "service fee";
  }

  function validateCartOwnership(cart, school, schoolOrderId) {
    const normalizedSchool = normalizeSchool(school);
    if (!normalizedSchool) {
      return { matches: false, reason: "MISSING_SCHOOL" };
    }

    const expectedHandle = normalizeIdentityValue(normalizedSchool.handle);
    const expectedCode = normalizeCodeIdentity(normalizedSchool.code);
    const expectedOrderId = normalizeIdentityValue(schoolOrderId);
    const items = (cart && Array.isArray(cart.items) ? cart.items : [])
      .filter((item) => !isServiceFeeItem(item));

    for (let index = 0; index < items.length; index += 1) {
      const properties = items[index].properties || {};
      const handle = normalizeIdentityValue(properties._yoc_school_handle);
      const code = normalizeCodeIdentity(
        properties["School Code"] || properties._school_code,
      );
      const orderId = normalizeIdentityValue(properties._school_order_Id);

      if (!handle && !code && !orderId) {
        return { matches: false, reason: "UNOWNED_ITEM" };
      }
      if (handle && handle !== expectedHandle) {
        return { matches: false, reason: "HANDLE_MISMATCH" };
      }
      if (code && expectedCode && code !== expectedCode) {
        return { matches: false, reason: "CODE_MISMATCH" };
      }
      if (orderId && expectedOrderId && orderId !== expectedOrderId) {
        return { matches: false, reason: "ORDER_ID_MISMATCH" };
      }
      if (orderId && !expectedOrderId && !handle && !code) {
        return { matches: false, reason: "UNVERIFIED_ORDER_ID" };
      }
    }

    return { matches: true, reason: "" };
  }

  function writeLegacySchoolState(school) {
    writeStorage(window.localStorage, "selectedSchool", school.handle);
    writeStorage(window.localStorage, "yoc_school_name", school.name);
    writeStorage(window.localStorage, "yoc_school_location", school.location);
    writeStorage(window.localStorage, "yoc_school_code", school.code);
    writeStorage(window.localStorage, "yoc_school_country", school.country);

    if (school.nameListEnabled == null) {
      removeStorage(window.localStorage, "yoc_name_list_enabled");
    } else {
      writeStorage(
        window.localStorage,
        "yoc_name_list_enabled",
        school.nameListEnabled ? "true" : "false",
      );
    }
  }

  function clearLegacySchoolState() {
    LEGACY_SCHOOL_KEYS.forEach((key) => {
      removeStorage(window.localStorage, key);
    });
  }

  function setActiveSchool(school, options) {
    const normalizedSchool = normalizeSchool(school);
    if (!normalizedSchool) {
      return null;
    }

    const sessionId = ensureSession();
    const context = {
      version: CONTEXT_VERSION,
      sessionId,
      revision: createId(),
      updatedAt: new Date().toISOString(),
      school: normalizedSchool,
    };

    if (
      !writeStorage(
        window.localStorage,
        ACTIVE_SCHOOL_KEY,
        JSON.stringify(context),
      )
    ) {
      return null;
    }

    writeLegacySchoolState(normalizedSchool);

    if (!options || options.broadcast !== false) {
      publish("SCHOOL_SELECTED", {
        school: normalizedSchool,
        revision: context.revision,
      });
    }

    return normalizedSchool;
  }

  function clearActiveSchool(options) {
    removeStorage(window.localStorage, ACTIVE_SCHOOL_KEY);
    clearLegacySchoolState();

    if (!options || options.broadcast !== false) {
      publish("SCHOOL_CLEARED", {});
    }
  }

  function endSession(options) {
    clearActiveSchool({ broadcast: false });
    clearPendingTarget();
    clearNavigationError();
    setChangeMode(false);
    deleteSessionCookie(SESSION_COOKIE_KEY);

    if (!options || options.broadcast !== false) {
      publish("SESSION_ENDED", {});
    }
  }

  function getPendingTarget() {
    const pending = parseJson(
      readStorage(window.sessionStorage, PENDING_TARGET_KEY),
    );

    if (!pending || pending.version !== CONTEXT_VERSION) {
      return null;
    }

    const school = normalizeSchool(pending.school);
    if (!school) {
      clearPendingTarget();
      return null;
    }

    return {
      school,
      sourceUrl: String(pending.sourceUrl || ""),
      createdAt: String(pending.createdAt || ""),
    };
  }

  function setPendingTarget(school, sourceUrl) {
    const normalizedSchool = normalizeSchool(school);
    if (!normalizedSchool) {
      return null;
    }

    const pending = {
      version: CONTEXT_VERSION,
      school: normalizedSchool,
      sourceUrl: String(sourceUrl || ""),
      createdAt: new Date().toISOString(),
    };

    if (
      !writeStorage(
        window.sessionStorage,
        PENDING_TARGET_KEY,
        JSON.stringify(pending),
      )
    ) {
      return null;
    }

    return pending;
  }

  function clearPendingTarget() {
    removeStorage(window.sessionStorage, PENDING_TARGET_KEY);
  }

  function isChangeMode() {
    return readStorage(window.sessionStorage, CHANGE_MODE_KEY) === "true";
  }

  function setChangeMode(enabled) {
    if (enabled) {
      writeStorage(window.sessionStorage, CHANGE_MODE_KEY, "true");
    } else {
      removeStorage(window.sessionStorage, CHANGE_MODE_KEY);
    }
  }

  function getNavigationError() {
    const navigationError = parseJson(
      readStorage(window.sessionStorage, NAVIGATION_ERROR_KEY),
    );

    if (!navigationError || navigationError.version !== CONTEXT_VERSION) {
      return null;
    }

    const code = String(navigationError.code || "").trim();
    if (!code) {
      clearNavigationError();
      return null;
    }

    return {
      code,
      detail:
        navigationError.detail && typeof navigationError.detail === "object"
          ? navigationError.detail
          : {},
      createdAt: String(navigationError.createdAt || ""),
    };
  }

  function setNavigationError(code, detail) {
    const normalizedCode = String(code || "").trim();
    if (!normalizedCode) {
      return null;
    }

    const navigationError = {
      version: CONTEXT_VERSION,
      code: normalizedCode,
      detail: detail && typeof detail === "object" ? detail : {},
      createdAt: new Date().toISOString(),
    };

    if (
      !writeStorage(
        window.sessionStorage,
        NAVIGATION_ERROR_KEY,
        JSON.stringify(navigationError),
      )
    ) {
      return null;
    }

    return navigationError;
  }

  function clearNavigationError() {
    removeStorage(window.sessionStorage, NAVIGATION_ERROR_KEY);
  }

  function rememberEvent(eventId) {
    receivedEventIds.add(eventId);

    if (receivedEventIds.size > 100) {
      const oldestEventId = receivedEventIds.values().next().value;
      receivedEventIds.delete(oldestEventId);
    }
  }

  function notify(event) {
    if (
      !event ||
      !event.id ||
      !event.type ||
      receivedEventIds.has(event.id)
    ) {
      return;
    }

    rememberEvent(event.id);
    listeners.forEach((listener) => {
      try {
        listener(event);
      } catch (error) {
        window.setTimeout(() => {
          throw error;
        }, 0);
      }
    });
  }

  function publish(type, detail) {
    const event = {
      id: createId(),
      type: String(type || ""),
      detail: detail && typeof detail === "object" ? detail : {},
      source: runtimeId,
      sessionId: getSessionId(),
      createdAt: new Date().toISOString(),
    };

    if (!event.type) {
      return null;
    }

    notify(event);

    if (channel) {
      try {
        channel.postMessage(event);
      } catch (error) {
        // The storage event below remains available as a fallback.
      }
    }

    writeStorage(
      window.localStorage,
      CONTEXT_EVENT_KEY,
      JSON.stringify(event),
    );

    return event;
  }

  function subscribe(listener) {
    if (typeof listener !== "function") {
      return function () {};
    }

    listeners.add(listener);
    return function () {
      listeners.delete(listener);
    };
  }

  function isLocalEvent(event) {
    return Boolean(event && event.source === runtimeId);
  }

  function handleStorageEvent(event) {
    if (!event || event.key !== CONTEXT_EVENT_KEY || !event.newValue) {
      return;
    }

    notify(parseJson(event.newValue));
  }

  if (typeof window.BroadcastChannel === "function") {
    try {
      channel = new window.BroadcastChannel(CHANNEL_NAME);
      channel.addEventListener("message", (event) => {
        notify(event && event.data);
      });
    } catch (error) {
      channel = null;
    }
  }

  window.addEventListener("storage", handleStorageEvent);

  window.yocSchoolContext = Object.freeze({
    classifySchoolRequest,
    clearActiveSchool,
    clearNavigationError,
    clearPendingTarget,
    endSession,
    ensureSession,
    getActiveSchool,
    getNavigationError,
    getPendingTarget,
    getSessionId,
    hasSession,
    isChangeMode,
    isLocalEvent,
    normalizeSchool,
    publish,
    setActiveSchool,
    setChangeMode,
    setNavigationError,
    setPendingTarget,
    schoolsMatch,
    subscribe,
    validateCartOwnership,
  });
})();
